<template>
  <div ref="dropdownRef" class="profile-dropdown-wrapper">
    <div class="profile-dropdown__trigger" @click="handleToggle">
      <slot />
      <VaAvatar :size="36" color="warning">
        <img v-if="userAvatar" :src="userAvatar" alt="Profile" />
        <span v-else>{{ userInitials }}</span>
      </VaAvatar>
    </div>

    <Transition
      name="dropdown"
      @beforeLeave="handleBeforeLeave"
      @afterLeave="handleAfterLeave"
      @enterCancelled="handleEnterCancelled"
      @leaveCancelled="handleLeaveCancelled"
    >
      <div v-if="isShown && !isUnmounting" class="profile-dropdown__content md:w-60 px-0 py-2 w-full">
        <!-- User Info Section -->
        <div class="user-info-section px-4 py-3 border-b border-[var(--va-background-border)]">
          <div class="flex items-center gap-3">
            <VaAvatar :size="44" color="primary">
              <img v-if="userAvatar" :src="userAvatar" alt="Profile" />
              <span v-else class="text-sm">{{ userInitials }}</span>
            </VaAvatar>
            <div class="flex flex-col min-w-0 flex-1">
              <span class="text-sm font-medium text-[var(--va-text-primary)] truncate">{{ userDisplayName }}</span>
              <span class="text-xs text-[var(--va-text-secondary)] truncate">{{ store.email }}</span>
              <span v-if="userRole" class="text-xs text-[var(--va-text-tertiary)] truncate">{{ userRole }}</span>
            </div>
          </div>
        </div>

        <!-- User Profile Section -->
        <div
          class="section-header px-4 py-2 text-xs font-medium text-[var(--va-text-secondary)] uppercase tracking-wider"
        >
          User Profile
        </div>
        <VaList>
          <VaListItem
            v-for="item in userProfileOptions"
            :key="item.name"
            class="menu-item px-4 text-sm cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="handleItemClick(item)"
          >
            <VaListItemSection icon>
              <VaIcon :name="item.icon" size="small" color="secondary" />
            </VaListItemSection>
            <VaListItemSection>
              <span class="text-[var(--va-text-primary)]">{{
                item.name === 'preferences' ? 'Preferences' : t(`user.${item.name}`)
              }}</span>
            </VaListItemSection>
          </VaListItem>
        </VaList>

        <!-- Logout Section -->
        <div class="logout-section px-4 py-2 border-t border-[var(--va-background-border)]">
          <VaListItem class="menu-item text-sm cursor-pointer h-8" @click="handleItemClick(logoutOption)">
            <VaListItemSection icon>
              <VaIcon :name="logoutOption.icon" size="small" color="danger" />
            </VaListItemSection>
            <VaListItemSection>
              <span class="text-[var(--va-text-danger)]">{{ t(`user.${logoutOption.name}`) }}</span>
            </VaListItemSection>
          </VaListItem>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, onBeforeUnmount, onMounted, nextTick, onUnmounted, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useUserStore } from '../../../../stores/user-store'
import { authService } from '../../../../services/authService'
import { showSuccess, showError } from '../../../../services/notificationService'

const store = useUserStore()
const router = useRouter()

const { t } = useI18n()

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  action?: () => void
}

const userProfileOptions: ProfileListItem[] = [
  {
    name: 'settings',
    to: 'settings',
    icon: 'settings',
  },
  {
    name: 'preferences',
    to: 'preferences',
    icon: 'manage_accounts',
  },
]

const logoutOption: ProfileListItem = {
  name: 'logout',
  icon: 'logout',
  action: async () => {
    try {
      // Call the logout endpoint first
      await authService.logout('Logged out successfully', router)
      showSuccess('Logged out successfully')
    } catch (error) {
      console.error('Logout error:', error)
      showError('Logout failed. Please try again.')
    }
  },
}

const isShown = ref(false)
const isUnmounting = ref(false)
const isMounted = ref(false)
const isTransitioning = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

onMounted(() => {
  isMounted.value = true
  // Add click outside listener with passive option for better performance
  document.addEventListener('click', handleClickOutside, { passive: true })
})

// Watch for unmounting state changes
watch(isUnmounting, (newValue) => {
  if (newValue && isShown.value) {
    isShown.value = false
  }
})

const handleClickOutside = (event: Event) => {
  if (!isMounted.value || isUnmounting.value || isTransitioning.value) return

  try {
    const target = event.target as HTMLElement
    if (dropdownRef.value && !dropdownRef.value.contains(target)) {
      isShown.value = false
    }
  } catch (error) {
    console.warn('Click outside handler error:', error)
    // Fallback: close dropdown on any error
    isShown.value = false
  }
}

const handleToggle = (event: Event) => {
  if (isUnmounting.value || !isMounted.value || isTransitioning.value) return

  // Prevent event bubbling
  event.stopPropagation()

  try {
    // Use a more defensive approach
    if (isShown.value) {
      isShown.value = false
    } else {
      // Ensure we're in a clean state before opening
      isTransitioning.value = false
      isShown.value = true
    }
  } catch (error) {
    console.warn('Dropdown toggle error:', error)
    // Reset to safe state
    isShown.value = false
    isTransitioning.value = false
  }
}

const handleBeforeLeave = () => {
  try {
    isTransitioning.value = true
  } catch (error) {
    console.warn('Before leave error:', error)
  }
}

const handleAfterLeave = () => {
  try {
    isTransitioning.value = false
  } catch (error) {
    console.warn('After leave error:', error)
  }
}

const handleEnterCancelled = () => {
  try {
    isTransitioning.value = false
    isShown.value = false
  } catch (error) {
    console.warn('Enter cancelled error:', error)
  }
}

const handleLeaveCancelled = () => {
  try {
    isTransitioning.value = false
  } catch (error) {
    console.warn('Leave cancelled error:', error)
  }
}

const handleItemClick = async (item: ProfileListItem) => {
  if (isUnmounting.value || !isMounted.value || isTransitioning.value) return

  try {
    // Set unmounting flag to prevent further interactions
    isUnmounting.value = true

    // Close dropdown first
    isShown.value = false

    // Wait for next tick to ensure dropdown is closed
    await nextTick()

    // Additional safety check
    if (!isMounted.value) return

    // Handle custom actions
    if (item.action) {
      await item.action()
    }
  } catch (error) {
    console.warn('Item click error:', error)
    isShown.value = false
  } finally {
    // Reset unmounting flag after a short delay
    setTimeout(() => {
      if (isMounted.value) {
        isUnmounting.value = false
      }
    }, 150) // Increased delay for better safety
  }
}

onBeforeUnmount(() => {
  isUnmounting.value = true
  isShown.value = false
})

onUnmounted(() => {
  isMounted.value = false
  isUnmounting.value = false
  isTransitioning.value = false
  // Remove click outside listener
  document.removeEventListener('click', handleClickOutside)
})

const resolveLinkAttribute = (item: ProfileListItem) => {
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}

const userFirstName = computed(() => localStorage.getItem('userFirstName') || '')
const userLastName = computed(() => localStorage.getItem('userLastName') || '')
const userRole = computed(() => store.role || localStorage.getItem('role') || '')
const userAvatar = computed(() => {
  const storedPath = store.pfp || localStorage.getItem('profileImagePath') || ''
  if (!storedPath) return ''

  if (storedPath.startsWith('http')) return storedPath

  const userId = localStorage.getItem('userId')
  if (userId) {
    return `${authService.getApi().defaults.baseURL}/auth/profile-image/${userId}`
  }

  return ''
})

const userDisplayName = computed(() => {
  if (userFirstName.value && userLastName.value) {
    return `${userFirstName.value} ${userLastName.value}`
  }
  if (store.userName && store.email && store.userName === store.email) {
    return store.email.split('@')[0]
  }
  return store.userName || 'User'
})

const userInitials = computed(() => {
  if (userFirstName.value && userLastName.value) {
    return (userFirstName.value.charAt(0) + userLastName.value.charAt(0)).toUpperCase()
  }
  return (store.userName?.charAt(0) || 'U').toUpperCase()
})

const processedUserName = computed(() => userDisplayName.value)
</script>

<style lang="scss" scoped>
.profile-dropdown-wrapper {
  position: relative;
  cursor: pointer;
}

.profile-dropdown {
  &__trigger {
    display: inline-block;
    transition: all 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  &__content {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 1000;
    background: var(--va-background-primary);
    border: 1px solid var(--va-background-border);
    border-radius: 8px;
    box-shadow: var(--va-card-box-shadow);
    min-width: 240px;
    margin-top: 8px;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);

    .menu-item {
      transition: all 0.2s ease;
      border-radius: 4px;
      margin: 2px 4px;

      &:hover {
        background: var(--va-background-element-hover);
        transform: translateX(2px);
      }

      &:active {
        transform: translateX(1px);
      }
    }
  }
}

.section-header {
  background-color: var(--va-background-secondary);
  border-radius: 4px;
  margin: 4px;
  opacity: 0.8;
}

.user-info-section {
  background-color: var(--va-background-secondary);
  border-radius: 4px;
  margin: 4px;
  opacity: 0.9;
}

.logout-section {
  background-color: var(--va-background-secondary);
  border-radius: 4px;
  margin: 4px;
  opacity: 0.9;

  .menu-item {
    &:hover {
      background: var(--va-danger) !important;
      color: #ffffff !important;

      span {
        color: #ffffff !important;
      }
    }
  }
}

// Dropdown transition
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
